import { supabase } from '@/integrations/supabase/client';
import { v4 as uuidv4 } from 'uuid';

interface LogEntry {
  level: 'log' | 'warn' | 'error' | 'info' | 'debug';
  message: string;
  meta?: any;
  timestamp: number;
}

const logQueue: LogEntry[] = [];
let flushTimeout: number | null = null;
const FLUSH_INTERVAL = 5000; // Send logs every 5 seconds
const BATCH_SIZE = 20; // Or when 20 logs are collected
let sessionId: string | null = null;

// Deduplicate identical logs within a short window to reduce noise
const lastLogTimestamps = new Map<string, number>();
const DEDUP_WINDOW_MS = 5000;

async function sendLogs() {
  if (logQueue.length === 0) {
    return;
  }

  const logsToSend = [...logQueue];
  logQueue.length = 0; // Clear the queue immediately

  if (flushTimeout) {
    clearTimeout(flushTimeout);
    flushTimeout = null;
  }

  try {
    const { error } = await supabase.functions.invoke('log-client-events', {
      body: {
        logs: logsToSend.map(log => ({
          level: log.level,
          message: log.message,
          meta: log.meta,
        })),
        sessionId,
      },
    });

    if (error) {
      // We use original console here to avoid a logging loop
      console.warn('[RemoteLogger] Failed to send logs:', error);
    }
  } catch (error) {
    console.warn('[RemoteLogger] Exception while sending logs:', error);
  }
}

function queueLog(level: LogEntry['level'], args: any[]) {
  const messageParts: string[] = [];
  let meta: any = {};
  const extraMeta: any[] = [];

  args.forEach(arg => {
    if (typeof arg === 'object' && arg !== null) {
      try {
        JSON.stringify(arg);
        extraMeta.push(arg);
      } catch (e) {
        messageParts.push('[Unserializable Object]');
      }
    } else {
      messageParts.push(String(arg));
    }
  });

  if (extraMeta.length > 0) {
    meta = extraMeta.length === 1 ? extraMeta[0] : extraMeta;
  }

  const message = messageParts.join(' ');
  const key = `${level}|${message}`;
  const now = Date.now();
  const last = lastLogTimestamps.get(key) || 0;
  if (now - last < DEDUP_WINDOW_MS) {
    return; // skip duplicate bursts
  }
  lastLogTimestamps.set(key, now);

  const baseMeta = {
    path: typeof window !== 'undefined' ? window.location?.pathname : undefined,
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
  };

  logQueue.push({
    level,
    message,
    meta: Object.keys(meta).length > 0 ? { ...baseMeta, ...meta } : baseMeta,
    timestamp: now,
  });

  if (logQueue.length >= BATCH_SIZE) {
    sendLogs();
  } else if (!flushTimeout) {
    flushTimeout = window.setTimeout(sendLogs, FLUSH_INTERVAL);
  }
}

export function initializeRemoteLogger() {
  // Only run in production and in the browser
  if (process.env.NODE_ENV !== 'production' || typeof window === 'undefined') {
    return;
  }

  // Ensure this runs only once
  if (window.hasOwnProperty('remoteLoggerInitialized')) {
    return;
  }
  Object.defineProperty(window, 'remoteLoggerInitialized', { value: true, writable: false });

  sessionId = uuidv4();

  const originalConsole = {
    log: console.log.bind(console),
    warn: console.warn.bind(console),
    error: console.error.bind(console),
    info: console.info.bind(console),
    debug: console.debug.bind(console),
  };

  console.log = (...args: any[]) => {
    // In production, logs are only sent to Supabase, not displayed in browser console
    queueLog('log', args);
  };

  console.warn = (...args: any[]) => {
    queueLog('warn', args);
  };

  console.error = (...args: any[]) => {
    queueLog('error', args);
  };
  
  console.info = (...args: any[]) => {
    queueLog('info', args);
  };

  console.debug = (...args: any[]) => {
    queueLog('debug', args);
  };

  window.addEventListener('unhandledrejection', event => {
    queueLog('error', ['Unhandled Rejection:', event.reason]);
  });

  window.addEventListener('error', event => {
    queueLog('error', ['Uncaught Exception:', event.message, {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error?.stack || event.error,
    }]);
  });

  originalConsole.log('[RemoteLogger] Initialized.');
}